import { api } from './api';

export type FraudStatus = 'OPEN' | 'CLEARED' | 'CONFIRMED';
export type FraudParty = 'DRIVER' | 'AGENT' | 'RIDER';

export interface FraudSignal {
    code: string;
    severity: number;
    detail: string;
    facts?: Record<string, number>;
}

/** Plain-English reading of a flag, written by server templates. */
export interface FraudExplanation {
    headline: string;
    certainty: 'High' | 'Medium' | 'Low';
    summary: string;
    mostLikely: 'driver' | 'agent' | 'rider' | null;
    recommendation: string;
    caveat: string;
}

/** Likelihood 0–100 and each party's share; shares need not add up to 100 (agentPct null: no agent). */
export interface FraudShares {
    fraudPct: number;
    driverPct: number | null;
    agentPct: number | null;
    riderPct: number | null;
}

export interface FraudReviewer {
    id: number;
    firstName: string | null;
    lastName: string | null;
}

/** A ride's flag from the nightly fraud review (never part of billing). */
export interface FraudFlag extends FraudShares {
    id: number;
    rideId: number;
    score: number;
    signals: FraudSignal[];
    rulesVersion: number;
    status: FraudStatus;
    driverUid: string | null;
    agentStaffId: number | null;
    riderUid: string | null;
    responsibleParties: string[];
    explanation: FraudExplanation;
    reviewedById: number | null;
    reviewedAt: string | null;
    note: string | null;
    createdAt: string;
    updatedAt: string;
    reviewedBy?: FraudReviewer | null;
}

export interface FraudFlagListItem extends FraudFlag {
    ride: {
        id: number;
        bookingSource: string | null;
        type: string | null;
        completedAt: string | null;
        totalFare: number | null;
        driverId: string | number | null;
        bookedByStaffId: number | null;
    };
    reviewedBy: FraudReviewer | null;
}

export type FraudSort = 'pct' | 'newest';

export interface FraudFlagPage {
    items: FraudFlagListItem[];
    /** The last id when newest first; a "pct_id" string when most likely first. Pass back verbatim. */
    nextCursor: number | string | null;
}

export interface FraudLabel extends FraudShares {
    score: number;
    status: FraudStatus;
    signals: FraudSignal[];
    explanation: FraudExplanation;
}

export interface FraudPerson {
    id: number;
    partyType: FraudParty;
    partyKey: string;
    name: string;
    rides: number;
    flaggedRides: number;
    avgPct: number;
    maxPct: number;
    lastFlaggedAt: string | null;
    updatedAt: string;
}

// GET flags of one status; sort 'pct' = most likely first, otherwise newest first
export const getFraudFlags = (params: { status?: FraudStatus; cursor?: number | string; limit?: number; sort?: 'pct' }) =>
    api.get<FraudFlagPage>('/admin/fraud/rides', { params });

// GET labels for a page of rides — only flagged rides come back
export const getFraudLabels = (rideIds: number[]) =>
    api.get<{ labels: Record<string, FraudLabel> }>('/admin/fraud/rides', { params: { rideIds: rideIds.join(',') } });

// GET one ride's flag ({ flag: null } when the ride is not flagged)
export const getFraudFlag = (rideId: number) =>
    api.get<{ flag: FraudFlag | null }>(`/admin/fraud/rides/${rideId}`);

// PATCH the verdict (fraud:review); CONFIRMED needs a 3+ char note and at least one responsible party
export const reviewFraudFlag = (rideId: number, status: FraudStatus, note: string, responsible: FraudParty[] = []) =>
    api.patch<FraudFlag>(`/admin/fraud/rides/${rideId}`, { status, note, responsible });

// GET the highest-risk people of one type over the last 30 days (rebuilt nightly)
export const getFraudPeople = (type: FraudParty, limit = 50) =>
    api.get<{ type: FraudParty; items: FraudPerson[] }>('/admin/fraud/people', { params: { type, limit } });
